import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import settingsApi from '../api/settingsApi';
import { useToast } from '../contexts/ToastContext';

function Footer() {
  const currentYear = new Date().getFullYear();
  const { addToast } = useToast();
  const [settings, setSettings] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (settingsApi && typeof settingsApi.getSettings === 'function') {
          const response = await settingsApi.getSettings();
          const fetchedSettings = response.data || {};
          setSettings(fetchedSettings);
        } else {
          console.error('settingsApi or getSettings is undefined in Footer.js');
          addToast('Failed to load footer links: API not available.', 'error');
        }
      } catch (error) {
        console.error('Failed to fetch settings for footer:', error);
        addToast('Failed to load footer links.', 'error');
      }
    };

    fetchSettings();
  }, [addToast]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/projects', label: 'Projects' },
    { path: '/faqs', label: 'FAQ' },
    { path: '/contact', label: 'Contact' },
  ];

  const quickConnectLinks = useMemo(() => {
    const links = [];
    if (settings && typeof settings === 'object') {
      if (settings.upwork_url) {
        links.push({ name: 'Upwork', url: settings.upwork_url });
      }
      if (settings.teams_url) {
        links.push({ name: 'Teams', url: settings.teams_url });
      }
      if (settings.email) {
        links.push({ name: 'Email', url: `mailto:${settings.email}` });
      }
      if (settings.linkedin_url) {
        links.push({ name: 'LinkedIn', url: settings.linkedin_url });
      }
      if (settings.github_url) {
        links.push({ name: 'GitHub', url: settings.github_url });
      }
    }
    return links;
  }, [settings]);

  const legalLinks = [
    { path: '/terms-conditions', label: 'Terms & Conditions' },
    { path: '/privacy-policy', label: 'Privacy Policy' },
  ];

  return (
      <footer className="bg-bg-dark text-text-gray py-10 border-t border-border-subtle">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 pb-10">
            <div className="col-span-1">
              <h3 className="text-xl font-bold text-text-white mb-4">Rishabh <span className="text-accent-red">Gupta</span></h3>
              <p className="text-sm leading-relaxed">
                Senior Software Engineer specializing in scalable digital solutions and robust web systems.
                Patches about crafting high-performance, enterprise-grade applications.
              </p>
            </div>

            <div className="col-span-1">
              <h3 className="text-xl font-bold text-text-white mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                    <li key={link.path}>
                      <Link to={link.path} className="text-text-gray hover:text-accent-red transition-colors duration-200">
                        {link.label}
                      </Link>
                    </li>
                ))}
              </ul>
            </div>

            <div className="col-span-1">
              <h3 className="text-xl font-bold text-text-white mb-4">Quick Connect</h3>
              <ul className="space-y-2">
                {quickConnectLinks.map((link) => (
                    <li key={link.name}>
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-text-gray hover:text-accent-red transition-colors duration-200">
                        {link.name}
                      </a>
                    </li>
                ))}
              </ul>
            </div>

            <div className="col-span-1">
              <h3 className="text-xl font-bold text-text-white mb-4">Legal</h3>
              <ul className="space-y-2">
                {legalLinks.map((link) => (
                    <li key={link.path}>
                      <Link to={link.path} className="text-text-gray hover:text-accent-red transition-colors duration-200">
                        {link.label}
                      </Link>
                    </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center pt-8 border-t border-border-subtle">
            <p className="text-sm">&copy; {currentYear} Rishabh Gupta. All rights reserved.</p>
          </div>
        </div>
      </footer>
  );
}

export default Footer;